import { useEffect } from 'react';

import {
  type SupplierSearchDataQuery,
  useSupplierSearchDataLazyQuery,
} from '../../../../../generated';
import { useDebounce, usePuppyMode } from '../../../../../hooks';

interface UseGetSuppliersQueryParams {
  searchTerm?: string;
  pageSize?: number;
  before?: string;
  after?: string;
}

type SearchSupplierConnection = Extract<
  NonNullable<SupplierSearchDataQuery['supplierSearch']>,
  { __typename: 'SearchSupplierConnection' }
>;

export const useGetSuppliersQuery = ({
  searchTerm = '',
  pageSize = 25,
  before = '',
  after = '',
}: UseGetSuppliersQueryParams = {}) => {
  const { puppyFilter } = usePuppyMode();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [executeSearch, { data, loading: isLoading, error, refetch }] =
    useSupplierSearchDataLazyQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    });

  useEffect(() => {
    executeSearch({
      variables: {
        search: debouncedSearchTerm || '',
        first: pageSize,
        before: before || '',
        after: after || '',
        filter: {
          isPuppy: puppyFilter ?? false,
        },
      },
    });
  }, [
    executeSearch,
    debouncedSearchTerm,
    pageSize,
    before,
    after,
    puppyFilter,
  ]);

  const supplierSearchResult = data?.supplierSearch;
  const isSearchSupplierConnection = (
    result: typeof supplierSearchResult
  ): result is SearchSupplierConnection => {
    return result?.__typename === 'SearchSupplierConnection';
  };

  const suppliers = isSearchSupplierConnection(supplierSearchResult)
    ? supplierSearchResult.edges.map((edge) => edge.node)
    : [];

  const total = isSearchSupplierConnection(supplierSearchResult)
    ? supplierSearchResult.total
    : 0;

  const pageInfo = isSearchSupplierConnection(supplierSearchResult)
    ? supplierSearchResult.pageInfo
    : {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: '',
        endCursor: '',
      };

  const isFetching = isLoading;

  return {
    data,
    suppliers,
    total,
    isLoading,
    isFetching,
    error,
    refetch,
    pageInfo,
  };
};
